<template>
  <hs-card class="ebm-stats" variant="flat">
    <div class="ebm-stats__header">
      <div>
        <p class="ebm-stats__eyebrow">EBM Requests</p>
        <h3>Transaction Statistics</h3>
        <span>Monitor request health, retry readiness, and status distribution.</span>
      </div>
      <div class="ebm-stats__actions">
        <hs-button variant="secondary" icon="calendar" @click="$emit('showDateFilter')">
          Date Filter
        </hs-button>
        <hs-button variant="secondary" icon="refresh-cw" @click="$emit('refresh')">
          Refresh
        </hs-button>
      </div>
    </div>

    <div v-if="loading" class="ebm-stats__loading">
      <hs-skeleton v-for="index in 4" :key="index" variant="card" />
    </div>

    <hs-alert
      v-else-if="error"
      tone="danger"
      title="Failed to load statistics"
      :message="error?.message || 'Unable to fetch transaction statistics.'"
    />

    <template v-else-if="stats">
      <div class="ebm-stats__metrics">
        <div
          v-for="metric in primaryMetrics"
          :key="metric.label"
          class="ebm-metric"
          :class="`ebm-metric--${metric.tone}`"
        >
          <hs-icon :name="metric.icon" :tone="metric.tone" soft :size="18" />
          <div>
            <span>{{ metric.label }}</span>
            <strong>{{ metric.value }}</strong>
          </div>
        </div>
      </div>

      <div class="ebm-stats__secondary">
        <div class="ebm-info-tile">
          <span>Store Timezone</span>
          <strong>{{ timezoneLabel }}</strong>
        </div>
        <div class="ebm-info-tile">
          <span>Oldest Failed</span>
          <strong>{{ formatDate(stats.oldest_failed_transaction) }}</strong>
        </div>
        <div class="ebm-info-tile">
          <span>Newest Failed</span>
          <strong>{{ formatDate(stats.newest_failed_transaction) }}</strong>
        </div>
      </div>

      <div class="ebm-stats__distributions">
        <section v-if="hasDistribution(stats.by_status)" class="ebm-distribution">
          <div class="ebm-distribution__title">
            <hs-icon name="pie-chart" :size="16" />
            Failed Transactions by Status
          </div>
          <div class="ebm-distribution__rows">
            <div v-for="(count, status) in stats.by_status" :key="status" class="ebm-distribution__row">
              <hs-badge :tone="getStatusTone(status)" size="sm">{{ status }}</hs-badge>
              <div class="ebm-distribution__bar">
                <span :style="{ width: `${getPercent(count, stats.total_failed)}%` }"></span>
              </div>
              <strong>{{ count }}</strong>
            </div>
          </div>
        </section>

        <section v-if="hasDistribution(stats.by_status_all)" class="ebm-distribution">
          <div class="ebm-distribution__title">
            <hs-icon name="bar-chart-2" :size="16" />
            All Transactions by Status
          </div>
          <div class="ebm-distribution__rows">
            <div v-for="(count, status) in stats.by_status_all" :key="status" class="ebm-distribution__row">
              <hs-badge :tone="getStatusTone(status)" size="sm">{{ status }}</hs-badge>
              <div class="ebm-distribution__bar">
                <span :style="{ width: `${getPercent(count, stats.total_transactions)}%` }"></span>
              </div>
              <strong>{{ count }}</strong>
            </div>
          </div>
        </section>

        <section v-if="hasDistribution(stats.by_transaction_type)" class="ebm-distribution">
          <div class="ebm-distribution__title">
            <hs-icon name="layers" :size="16" />
            Failed Transactions by Type
          </div>
          <div class="ebm-distribution__rows">
            <div v-for="(count, type) in stats.by_transaction_type" :key="type" class="ebm-distribution__row">
              <span class="ebm-distribution__label">{{ formatTransactionType(type) }}</span>
              <div class="ebm-distribution__bar">
                <span :style="{ width: `${getPercent(count, stats.total_failed)}%` }"></span>
              </div>
              <strong>{{ count }}</strong>
            </div>
          </div>
        </section>
      </div>
    </template>

    <hs-empty-state
      v-else
      icon="bar-chart-2"
      title="No transaction statistics"
      description="Statistics will appear when EBM transactions are available."
    />
  </hs-card>
</template>

<script>
import { HsAlert, HsBadge, HsButton, HsCard, HsEmptyState, HsIcon, HsSkeleton } from "@/components/ui";

export default {
  name: "EbmTransactionStatsCard",
  components: {
    HsAlert,
    HsBadge,
    HsButton,
    HsCard,
    HsEmptyState,
    HsIcon,
    HsSkeleton,
  },
  props: {
    stats: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: [Object, String],
      default: null,
    },
  },
  emits: ["refresh", "showDateFilter"],
  computed: {
    primaryMetrics() {
      const stats = this.stats || {};
      return [
        { label: "Total", value: stats.total_transactions || 0, tone: "primary", icon: "activity" },
        { label: "Failed", value: stats.total_failed || 0, tone: "danger", icon: "alert-circle" },
        { label: "Ready for Retry", value: stats.ready_for_retry || 0, tone: "warning", icon: "refresh-cw" },
        { label: "Success Rate", value: `${Number(stats.success_rate || 0).toFixed(1)}%`, tone: "success", icon: "check-circle" },
      ];
    },
    timezoneLabel() {
      const minutes = this.stats?.timezone_offset_minutes;
      if (minutes === null || minutes === undefined) return "Unknown";
      return `UTC${minutes >= 0 ? "+" : ""}${Math.round(minutes / 60)}`;
    },
  },
  methods: {
    hasDistribution(value) {
      return value && Object.keys(value).length > 0;
    },
    getPercent(count, total) {
      if (!total) return 0;
      return Math.max(0, Math.min(100, (count / total) * 100));
    },
    formatDate(dateString) {
      if (!dateString) return "None";
      try {
        return new Date(dateString).toLocaleString();
      } catch (error) {
        return "Invalid date";
      }
    },
    formatTransactionType(type) {
      if (!type) return "Unknown";
      return String(type)
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    getStatusTone(status) {
      const tones = {
        FAILED: "danger",
        PARTIALLY_COMPLETED: "warning",
        PENDING: "warning",
        IN_PROGRESS: "info",
        COMPLETED: "success",
      };
      return tones[status] || "neutral";
    },
  },
};
</script>

<style scoped>
.ebm-stats {
  display: grid;
  gap: 1rem;
}

.ebm-stats__header {
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: space-between;
}

.ebm-stats__eyebrow {
  color: var(--hs-primary, #3293ec);
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.08em;
  margin: 0 0 0.25rem;
  text-transform: uppercase;
}

.ebm-stats h3 {
  color: var(--hs-text, #0f172a);
  font-size: 1.1rem;
  font-weight: 950;
  margin: 0;
}

.ebm-stats__header span {
  color: var(--hs-muted, #64748b);
  display: block;
  font-size: 0.84rem;
  font-weight: 700;
  margin-top: 0.25rem;
}

.ebm-stats__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
}

.ebm-stats__loading,
.ebm-stats__metrics {
  display: grid;
  gap: 0.85rem;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 12rem), 1fr));
}

.ebm-metric {
  align-items: center;
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 58%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
}

.ebm-metric span,
.ebm-info-tile span {
  color: var(--hs-muted, #64748b);
  display: block;
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.ebm-metric strong {
  color: var(--hs-text, #0f172a);
  display: block;
  font-size: 1.35rem;
  font-weight: 950;
  line-height: 1;
  margin-top: 0.35rem;
}

.ebm-stats__secondary {
  display: grid;
  gap: 0.85rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.ebm-info-tile {
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 94%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  padding: 0.9rem;
}

.ebm-info-tile strong {
  color: var(--hs-text, #0f172a);
  display: block;
  font-size: 0.9rem;
  font-weight: 850;
  margin-top: 0.35rem;
}

.ebm-stats__distributions {
  display: grid;
  gap: 0.85rem;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 19rem), 1fr));
}

.ebm-distribution {
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 54%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  display: grid;
  gap: 0.8rem;
  padding: 1rem;
}

.ebm-distribution__title {
  align-items: center;
  color: var(--hs-text, #0f172a);
  display: flex;
  font-size: 0.9rem;
  font-weight: 950;
  gap: 0.45rem;
}

.ebm-distribution__rows {
  display: grid;
  gap: 0.6rem;
}

.ebm-distribution__row {
  align-items: center;
  display: grid;
  gap: 0.65rem;
  grid-template-columns: minmax(7rem, 1fr) minmax(5rem, 8rem) auto;
}

.ebm-distribution__label {
  color: var(--hs-text, #0f172a);
  font-size: 0.82rem;
  font-weight: 800;
}

.ebm-distribution__bar {
  background: color-mix(in srgb, var(--hs-border, #cbd5e1) 62%, transparent);
  border-radius: 999px;
  height: 0.45rem;
  overflow: hidden;
}

.ebm-distribution__bar span {
  background: var(--hs-primary, #3293ec);
  border-radius: inherit;
  display: block;
  height: 100%;
}

.ebm-distribution__row strong {
  color: var(--hs-text, #0f172a);
  font-size: 0.84rem;
  font-weight: 950;
  text-align: right;
}

@media (max-width: 760px) {
  .ebm-stats__secondary {
    grid-template-columns: 1fr;
  }

  .ebm-distribution__row {
    grid-template-columns: 1fr auto;
  }

  .ebm-distribution__bar {
    grid-column: 1 / -1;
    grid-row: 2;
  }
}
</style>
